"use client";

import { useCallback, useRef, useState } from "react";

interface DragHandleProps {
	onDrag: (offset: number) => void;
	onClose: () => void;
	className?: string;
}

export function DragHandle({ onDrag, onClose, className }: DragHandleProps) {
	const [isDragging, setIsDragging] = useState(false);
	const startY = useRef(0);
	const currentDragY = useRef(0);

	const handleTouchStart = useCallback((e: React.TouchEvent) => {
		// STOP PROPAGATION IMMEDIATELY to prevent parent from seeing this
		e.stopPropagation();

		startY.current = e.touches[0].clientY;
		currentDragY.current = 0;
		setIsDragging(true);
	}, []);

	const handleTouchMove = useCallback(
		(e: React.TouchEvent) => {
			if (!isDragging) return;
			e.stopPropagation();

			const currentY = e.touches[0].clientY;
			const deltaY = currentY - startY.current;

			// Only allow dragging downwards
			if (deltaY > 0) {
				e.preventDefault(); // Prevent scrolling
				currentDragY.current = deltaY;
				onDrag(deltaY);
			}
		},
		[isDragging, onDrag],
	);

	const handleTouchEnd = useCallback(
		(e: React.TouchEvent) => {
			e.stopPropagation();
			setIsDragging(false);

			if (currentDragY.current > 100) {
				onClose();
			} else {
				// Reset position
				onDrag(0);
			}
			currentDragY.current = 0;
		},
		[onClose, onDrag],
	);

	// Mouse handlers for testing/desktop support
	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault(); // Prevent text selection
		startY.current = e.clientY;
		currentDragY.current = 0;
		setIsDragging(true);
	}, []);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => {
			if (!isDragging) return;
			e.stopPropagation();
			e.preventDefault();

			const deltaY = e.clientY - startY.current;
			if (deltaY > 0) {
				currentDragY.current = deltaY;
				onDrag(deltaY);
			}
		},
		[isDragging, onDrag],
	);

	const handleMouseUp = useCallback(
		(e: React.MouseEvent) => {
			if (!isDragging) return;
			e.stopPropagation();
			setIsDragging(false);

			if (currentDragY.current > 100) {
				onClose();
			} else {
				onDrag(0);
			}
			currentDragY.current = 0;
		},
		[isDragging, onClose, onDrag],
	);

	// We need a global mouse up listener because the mouse can leave the element while dragging
	// But since we want to be "strictly on this element", standard UX is usually capture.
	// For simplicity and strict isolation requested by user, we'll keep listeners here.
	// If mouse leaves the handle, we can stop or handle "onMouseLeave".
	// Let's handle mouse leave to stop drag to be safe and simple.

	const handleMouseLeave = useCallback(
		(e: React.MouseEvent) => {
			if (isDragging) {
				e.stopPropagation();
				setIsDragging(false);
				onDrag(0);
				currentDragY.current = 0;
			}
		},
		[isDragging, onDrag],
	);

	return (
		<div
			className={`flex justify-center p-2 flex-shrink-0 cursor-grab active:cursor-grabbing select-none touch-none ${className || ""}`}
			onTouchStart={handleTouchStart}
			onTouchMove={handleTouchMove}
			onTouchEnd={handleTouchEnd}
			onMouseDown={handleMouseDown}
			onMouseMove={handleMouseMove}
			onMouseUp={handleMouseUp}
			onMouseLeave={handleMouseLeave}
		>
			<div className="w-8 h-1 bg-gray-300 rounded-full" />
		</div>
	);
}
